import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import { SheetFilters } from '@/common/components/SheetFilters'
import TextField from '@/common/components/TextField'
import { equipmentStatusTranslation } from '@/common/constants/equipmentStatus.constants'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { options as propertyKindOptions } from '@/features/equipments/constants/propertyKindOptions.constant'
import { options as usageKindOptions } from '@/features/equipments/constants/usageKindOptions.constant'
import { CarFront, SearchIcon } from 'lucide-react'
import { useEquipmentCategories } from '../../hooks/useEquipmentCategories'
import { useEquipments } from '../../hooks/useEquipments'
import { EquipmentCategoryData, EquipmentData } from '../../types/equipments.type'
import { EquipmentsGridTable } from '@/features/equipments/components/EquipmentsGridTable'
import type { CompanyBranchData } from '@/common/types/companyBranches.type'
import Button from '@/common/components/Button'
import { useAvailableCompanyBranches } from '@/features/companyBranches/hooks/useAvailableCompanyBranches'
import { useNavigate } from 'react-router-dom'
import { ChangeEvent, useMemo } from 'react'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import QueryString from 'qs'

const EquipmentsList = () => {
  const navigate = useNavigate()

  const { filterParams, setFilterParams } = useFilterParams({
    status: { label: 'Ativo', value: 'enabled' }
  });
  const hasFilterParmas = Object.keys(filterParams).some((key) => key !== 'page')

  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')
  const [searchEquipmentBranch, setSearchEquipmentBranch, instantSearchEquipmentBranch] = useDebounce('')
  const [daysWithoutUsage, setDaysWithoutUsage] = useDebounce(filterParams.daysWithoutUsage || '');

  const {
    data: companyEquipments,
    meta,
    isPending: companyEquipmentsIsPending,
    error: companyEquipmentsError = {} as any,
    refetch: refetchCompanyEquipments,
  } = useEquipments<EquipmentData[]>({
    filters: {
      search: filterParams.search,
      page: filterParams.page,
      status: filterParams.status?.value,
      categoryId: filterParams.category?.value,
      branchId: filterParams.branch?.value,
      usageKind: filterParams.usage_kind?.value,
      propertyKind: filterParams.property_kind?.value,
      daysWithoutUsage: daysWithoutUsage,
    },
  })

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: branchOptions } = useAvailableCompanyBranches({
    filters: { search: searchEquipmentBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const handleChangeDaysWithoutUsage = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? parseInt(e.target.value) : ''
    if (typeof value === 'number' && (value < 1 || value > 730)) return
    setDaysWithoutUsage(`${value}`)
    setFilterParams((prev) => ({ ...prev, daysWithoutUsage: `${value}` }))
  }

  const { hasUserPermissionWithProperty, isUserPermissionsPending } = useUserPermissions();
  
  const hasPermissionToCreateEquipmentsReport = useMemo(() => {
    return hasUserPermissionWithProperty({
      operationKlass: 'Client::Reports::Entries::Create',
      propertyKey: 'generator_klass',
      propertyValue: 'Reports::Client::EquipmentsReport',
    });
  }, [isUserPermissionsPending]);

  const handleNavigateToReportsScreen = () => {
    const reportUrl = QueryString.stringify({
      generator_klass: 'Reports::Client::EquipmentsReport',
      q: {
        ...(filterParams?.category && {
          equipment_category_id_eq: {
            label: filterParams.category.label,
            value: filterParams.category.value,
          }
        }),

        ...(filterParams?.branch && {
          client_branch_id_eq: {
            label: filterParams.branch.label,
            value: filterParams.branch.value,
          }
        }),
        
        ...(filterParams?.status && {
          equipment_status_eq: {
            label: filterParams.status.label,
            value: filterParams.status.value,
          }
        }),
        
        ...(filterParams?.daysWithoutUsage && {
          days_without_usage_gteq: filterParams.daysWithoutUsage
        }),

        ...(filterParams?.usage_kind && {
          usage_kind_eq: {
            label: filterParams.usage_kind.label,
            value: filterParams.usage_kind.value,
          }
        }),
        
        ...(filterParams?.property_kind && {
          property_kind_eq: {
            label: filterParams.property_kind.label,
            value: filterParams.property_kind.value,
          }
        }),
      },
    });

    navigate(`/reports/create?${reportUrl}`);
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<CarFront className="w-10 h-10" strokeWidth={1.5} />}
        title="Equipamentos"
        createButtonText="Novo equipamento"
        dataTestid='create_button'
        disableCreateButton={companyEquipmentsIsPending || companyEquipmentsError?.status === 403}
      />

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={companyEquipmentsError?.status}
        refetchFunction={refetchCompanyEquipments}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              value={filterParams.search || ''}
              onChange={(e: any) => setFilterParams((prev) => ({ ...prev, search: e.target?.value }))}
            />

            <div className='flex items-center justify-between'>
              <div className="flex gap-2 flex-wrap items-center justify-start">
                <ListFilters
                  filters={[
                    {
                      className: '',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, category: e })),
                      onInputChange: setSearchEquipmentCategory,
                      options: [{ label: 'Todas', value: '' }, ...(equipmentCategoryOptions || [])],
                      value: filterParams.category || '',
                      placeholder: 'Tipo de Equipamento',
                    },
                    {
                      className: '',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                      options: [
                        { label: 'Todas', value: '' },
                        ...Object.entries(equipmentStatusTranslation).map(([key, value]) => ({
                          label: value,
                          value: key,
                        })),
                      ],
                      value: filterParams.status || '',
                      placeholder: 'Status',
                    },
                    {
                      className: '',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, branch: e })),
                      onInputChange: setSearchEquipmentBranch,
                      options: [{ label: 'Todas', value: '' }, ...branchOptions || []],
                      value: filterParams.branch || '',
                      placeholder: 'Filial',
                    },
                  ]}
                />

                <SheetFilters
                  filters={[
                    {
                      label: 'Tipo de Equipamento',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: filterParams.category?.value === '',
                          onClick: () => setFilterParams((prev) => ({ ...prev, category: '' })),
                        },
                        ...(equipmentCategoryOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.category?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, category: { value, label } })),
                        })) || []),
                      ],
                    },
                    {
                      label: 'Status',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: filterParams.status?.value === '',
                          onClick: () => setFilterParams((prev) => ({ ...prev, status: '' })),
                        },
                        ...Object.entries(equipmentStatusTranslation).map(([key, value]) => ({
                          label: value,
                          value: key,
                          selected: filterParams.status?.value === key,
                          onClick: () => setFilterParams((prev) => ({ ...prev, status: { value: key, label: value } })),
                        })),
                      ],
                    },
                    {
                      label: 'Dias sem compra',
                      component: (
                        <TextField
                          placeholder="Dias sem compra"
                          type="number"
                          min={1}
                          max={730}
                          value={filterParams.daysWithoutUsage}
                          onChange={handleChangeDaysWithoutUsage}
                        />
                      )
                    },
                    {
                      label: 'Tipo de uso',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: filterParams.usage_kind === '',
                          onClick: () => setFilterParams((prev) => ({ ...prev, usage_kind: '' })),
                        },
                        ...usageKindOptions.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.usage_kind?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, usage_kind: { value, label } })),
                        })),
                      ],
                    },
                    {
                      label: 'Tipo de propriedade',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: filterParams.property_kind === '',
                          onClick: () => setFilterParams((prev) => ({ ...prev, property_kind: '' })),
                        },
                        ...propertyKindOptions.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.property_kind?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, property_kind: { value, label } })),
                        })),
                      ],
                    },
                    {
                      label: 'Filial',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: !filterParams.branch?.value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, branch: null }))
                        },
                        ...(branchOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.branch?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, branch: { label, value } }))
                        })) || [])
                      ],
                      component: (
                        <TextField
                          variant="filter"
                          LeftIcon={<SearchIcon size={14} />}
                          placeholder="Pesquisar"
                          className="mb-2"
                          value={instantSearchEquipmentBranch}
                          onChange={(e) => setSearchEquipmentBranch(e.currentTarget.value)}
                        />
                      ),
                    },
                  ]}
                />
                {hasFilterParmas && (
                  <Button
                    variant='link'
                    className="px-4"
                    onClick={() => setFilterParams(() => ({}))}
                  >
                    Limpar Filtros
                  </Button>
                )}
              </div>

              <Button 
                className="transition-colors border border-blue-800 text-blue-800 bg-transparent hover:bg-blue-800/10 disabled:hover:bg-transparent"
                onClick={handleNavigateToReportsScreen}
                disabled={!hasPermissionToCreateEquipmentsReport}
                tooltipMessage={!hasPermissionToCreateEquipmentsReport ? 'Você não tem permissão' : undefined}
              >
                Extrair equipamentos
              </Button>
            </div>
          </div>

          <div>
            <EquipmentsGridTable
              className="max-h-[calc(100vh-280px)]"
              companyEquipments={companyEquipments || []}
              page={filterParams.page || 1}
              totalPages={meta.total_pages || 1}
              totalCount={meta.total}
              setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
              isLoadingData={companyEquipmentsIsPending}
            />
          </div>
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default EquipmentsList
